import './FileUploadField.scss';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Stack, IconButton, Typography, useTheme } from '@mui/material';

import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import pdfIcon from '../../assets/images/pdfIcon.png';
import xlsIcon from '../../assets/images/xls.png';
import FileViewer from '../FileViewer/FileViewer';

function FileUploadField({ handleFile, inputId, url, placeholder, helpTextShow = false, showFileName = true, disableDownload = true, bgColor, height, fileName, enableExcel = false }) {
  const [error, setError] = useState('');

  const theme = useTheme();

  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (url && url.type) {
      const fileType = url.type.startsWith('image') ? 'image' : 'pdf';
      if (fileType === 'image') {
        const image = new Image();
        image.src = URL.createObjectURL(url);
        image.addEventListener('load', () => {
          let objUrl = URL.createObjectURL(url);
          setFilePreview(objUrl);
          setFileType(fileType);
        });
      } else {
        const isPdf = url.type === 'application/pdf';
        if(isPdf) {
          setFilePreview(pdfIcon);
          setFileType(fileType);
        } else {
          setFilePreview(xlsIcon);
          setFileType('xls');
        }
        
      }
    } else {
      if (url) {
        const type = url.split('.').pop();
        if (type === 'jpg' || type === 'png' || type === 'jpeg' || type === 'webp' ) {
          setFilePreview(url);
        } else {
          if(type === 'pdf') {
            setFilePreview(pdfIcon);
          } else {
            setFilePreview(xlsIcon);
          }
        }
      } else {
        setFilePreview(null);
      }
    }
  }, [url]);

  const onDrop = async (files) => {
    if(!files || files.length === 0) {
      return;
    }
    if (files[0].size > 5000000) {
      handleFile(null);
      setError('File size should be less than or equal to 5MB');
      setFile(null);
      setFilePreview(null);
      return;
    }
    const fileType = files[0].type.startsWith('image') ? 'image' : 'pdf';
    if (fileType === 'image') {
      const image = new Image();
      image.src = URL.createObjectURL(files[0]);
      image.addEventListener('load', () => {
        handleFile(files[0]);
        setFile(files[0]);
        let objUrl = URL.createObjectURL(files[0]);
        setFilePreview(objUrl);
        setFileType(fileType);
        setError(false);
      });
    } else {
      handleFile(files[0]);
      setFile(files[0]);
      setError(false);
      const isPdf = files[0].type === 'application/pdf';
      
        if(isPdf) {
          setFilePreview(pdfIcon);
          setFileType(fileType);
        } else {
          setFilePreview(xlsIcon);
          setFileType('xls');
        }
    }
  };

  const onCancelFile = () => {
    handleFile(null);
    setFilePreview(null);
    setError(false);
    setFile(null);
    setFileType(null);
  };

  const excelExtenstion =  enableExcel ? {
    'application/vnd.ms-excel': ['.xls', '.xlsx']
  } : {};

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.png', '.webp', '.jpg'],
      'apllication/pdf': ['.pdf'],
      ...excelExtenstion
    },
    onDrop,
    maxFiles: 1,
  });

  const handleFileOpener = () => {
    if (file) {
      if (fileType === 'image') {
        setOpenModal(true);
      } else {
        const blob = new Blob([file], { type: file.type });
        const dataUrl = window.URL.createObjectURL(blob);
        window.open(dataUrl, '_blank', 'noreferrer');
      }
    } else {
      const type = url.split('.').pop();
      if (type === 'jpg' || type === 'png' || type === 'jpeg' || type === 'webp') {
        setOpenModal(true);
      } else {
        window.open(url, '_blank', 'noreferrer');
      }
    }
  };

  return (
    <Stack
      alignItems="center"
      className="w-full"
    >
      <Stack
        className="uploadContainer w-full"
        style={{ height: height }}
      >
        {!(file || url) && (
          <div
            className="dropZone"
            style={{ cursor: 'pointer', width: '100%', height: '100%' }}
            {...getRootProps({ className: 'dropzone' })}
          >
            <Stack
              alignItems="center"
              className="dropZone !px-[16px]"
              direction={'row'}
              justifyContent={'space-between'}
              style={{ backgroundColor: bgColor }}
            >
              <input
                id={inputId}
                {...getInputProps()}
              />
              <Typography
                className="fs-14 ff-Roboto fw-medium"
                color={theme.palette.primary.text}
              >
                {placeholder}
              </Typography>
              <FileUploadOutlinedIcon style={{ color: theme.palette.primary.text }} />
            </Stack>
          </div>
        )}
        {!Boolean(error) && (file || url) && (
          <Stack
            flexDirection="row"
            alignItems="center"
            className="dropZone"
            justifyContent="center"
          >
            <Stack className="previewContainer">
              <img
                alt=""
                src={filePreview || url}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              <Stack
                className="previewOverlay"
                alignItems="center"
                justifyContent="center"
                onClick={() => handleFileOpener()}
              >
                <VisibilityIcon style={{ color: '#fff', fontSize: '16px' }} />
              </Stack>
            </Stack>
            {showFileName && (
              <Stack sx={{ maxWidth: '60%', ml: '10px' }}>
                <Typography
                  className="fs-12 ff-Roboto fw-medium truncate"
                  style={{ color: '#666666' }}
                >
                  {fileName ? fileName : file ? file.name : url}
                </Typography>
              </Stack>
            )}
            <IconButton onClick={() => onCancelFile()}>
              <ClearIcon style={{ fontSize: '16px' }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
      {Boolean(error) && (
        <Typography
          className="fs-10 ff-Roboto fw-regular !mt-2.5"
          color={theme.palette.error.main}
          sx={{alignSelf: 'flex-start'}}
        >
          {error}
        </Typography>
      )}
      {helpTextShow && (
        <Typography
          className="fs-10 ff-Roboto fw-regular !mt-2.5"
          color={'#666666'}
          sx={{alignSelf: 'flex-start'}}
        >
          (Maximum file size 5mb or below. Supported format Jpg, Jpeg, Png, Pdf)
        </Typography>
      )}
      {openModal && (
        <FileViewer
          file={filePreview || url}
          closeModal={() => setOpenModal(false)}
          open={openModal}
          disableDownload={disableDownload}
        />
      )}
    </Stack>
  );
}

export default FileUploadField;
